import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, ButtonBase, Divider, Stack, Typography } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import { QuestionnaireStatus } from "Infrastructure/ApiZfp/Schema";
import { useFormatLocale } from "Infrastructure/Hooks/DateFns/useFormatLocale";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { getCzechPluralForm, inflectMonths } from "Infrastructure/Utils/LanguageUtils";
import { differenceInMonths } from "date-fns";
import type { ReactNode } from "react";
import { Tag } from "../_Shared/Tag";
import { useIsPrintMode } from "./QuestionnaireContext";
import { QuestionnairePdfActions } from "./QuestionnairePdfActions";
import { getQuestionnaireValidUntil } from "./utils";

type ActionCardProps = {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

const ActionCard = ({ icon, label, onClick, disabled }: ActionCardProps) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    sx={{
      width: "100%",
      justifyContent: "flex-start",
      gap: Spacing.L,
      px: Spacing.L,
      py: Spacing.L,
      textAlign: "left",
      bgcolor: "white",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: Radius.L,
      transition: "border-color 0.15s",
      "&:hover": { borderColor: "common.black" },
      "&.Mui-disabled": { opacity: 0.5 },
    }}
  >
    <Flex color="text.primary">{icon}</Flex>
    <Typography variant="body2" fontWeight={500} flex={1}>
      {label}
    </Typography>
    <ChevronRightIcon fontSize="small" sx={{ color: "text.secondary" }} />
  </ButtonBase>
);

type InfoRowProps = {
  icon: ReactNode;
  label: string;
  value: ReactNode;
};

const InfoRow = ({ icon, label, value }: InfoRowProps) => (
  <Flex alignItems="center" gap={Spacing.L} py={Spacing.L}>
    <Flex color="text.secondary">{icon}</Flex>
    <Typography variant="body2" flex={1}>
      {label}
    </Typography>
    {typeof value === "string" ? (
      <Typography variant="body2" fontWeight={500} noWrap>
        {value}
      </Typography>
    ) : (
      value
    )}
  </Flex>
);

type Props = {
  status: QuestionnaireStatus;
  evaluatedAt?: string | null;
  signedAt?: string | null;
  canChangeAnswers: boolean;
  isChangeAnswersDisabled: boolean;
  onChangeAnswers: () => void;
  onShowQuestionnaire: () => void;
};

export const QuestionnaireEvaluationSidePanel = ({
  status,
  evaluatedAt,
  signedAt,
  canChangeAnswers,
  isChangeAnswersDisabled,
  onChangeAnswers,
  onShowQuestionnaire,
}: Props) => {
  const { t } = useResource();
  const formatDate = useFormatLocale();
  const isPrint = useIsPrintMode();
  const isEvaluated = status === QuestionnaireStatus.evaluated;

  const validUntil = evaluatedAt ? getQuestionnaireValidUntil(new Date(evaluatedAt)) : null;
  const remainingMonths = validUntil ? differenceInMonths(validUntil, new Date()) : 0;

  const questionnaireStatus = (
    <Stack spacing={Spacing.L}>
      <Typography variant="subtitle1" fontWeight={600}>
        {t(Resources.Questionnaire.Evaluation.Status.Title)}
      </Typography>
      <Stack divider={<Divider />} px={Spacing.L} bgcolor="white" border="1px solid" borderColor="divider" borderRadius={Radius.L}>
        <InfoRow
          icon={<CalendarTodayOutlinedIcon fontSize="small" />}
          label={t(Resources.Questionnaire.Evaluation.Status.LastUpdatedAt)}
          value={evaluatedAt ? formatDate(new Date(evaluatedAt), "d.M.yyyy") : "-"}
        />
        {isEvaluated && (
          <InfoRow
            icon={<EventAvailableOutlinedIcon fontSize="small" />}
            label={t(Resources.Questionnaire.Evaluation.Status.ValidUntil)}
            value={
              validUntil ? (
                <Stack alignItems="flex-end">
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {formatDate(validUntil, "d.M.yyyy")}
                  </Typography>
                  {remainingMonths > 0 && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {t(
                        getCzechPluralForm(remainingMonths, {
                          one: Resources.Questionnaire.Evaluation.Status.RemainingOne,
                          few: Resources.Questionnaire.Evaluation.Status.RemainingFew,
                          other: Resources.Questionnaire.Evaluation.Status.RemainingOther,
                        }),
                        { duration: inflectMonths(remainingMonths, t) },
                      )}
                    </Typography>
                  )}
                </Stack>
              ) : (
                "-"
              )
            }
          />
        )}
        {isEvaluated && (
          <InfoRow
            icon={<HistoryEduOutlinedIcon fontSize="small" />}
            label={t(Resources.Questionnaire.Evaluation.Status.Signed)}
            value={
              signedAt ? (
                <Typography variant="body2" fontWeight={500} noWrap>
                  {formatDate(new Date(signedAt), "d.M.yyyy")}
                </Typography>
              ) : (
                <Tag title={t(Resources.Questionnaire.Evaluation.Status.NotSigned)} color="warning" variant="solid" />
              )
            }
          />
        )}
      </Stack>
    </Stack>
  );

  if (isPrint) {
    return <Box sx={{ width: "380px", flexShrink: 0 }}>{questionnaireStatus}</Box>;
  }

  return (
    <Box px={Spacing.XL} sx={{ flexShrink: 0, alignSelf: "flex-start" }}>
      <Stack
        spacing={Spacing.XL}
        divider={<Divider />}
        width="380px"
        boxSizing="border-box"
        p={Spacing.XXL}
        bgcolor="grey.50"
        border="1px solid"
        borderColor="divider"
        borderRadius={Radius.L}
      >
        <Stack spacing={Spacing.M}>
          {canChangeAnswers && (
            <ActionCard
              icon={<EditOutlinedIcon fontSize="small" />}
              label={t(Resources.Questionnaire.Evaluation.ChangeAnswers)}
              disabled={isChangeAnswersDisabled}
              onClick={onChangeAnswers}
            />
          )}
          <ActionCard
            icon={<VisibilityOutlinedIcon fontSize="small" />}
            label={t(Resources.Questionnaire.Evaluation.ShowQuestionnaire)}
            onClick={onShowQuestionnaire}
          />
        </Stack>
        {questionnaireStatus}
        <QuestionnairePdfActions isEvaluated={isEvaluated} />
      </Stack>
    </Box>
  );
};
